"use client";

import dynamic from "next/dynamic";
import Image from "next/image";
import { memo } from "react";
import { Play, Film, Info, ImageOff, VideoOff } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSyncExternalStore } from "react";
import Link from "next/link";
import { ProseLink } from "@/components/ProseLink";
import { FilterChip } from "@/components/FilterChip";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useGalleryPageSize } from "@/hooks/use-gallery-page-size";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useGallery } from "@/stores/gallery-store";
import { useVideoBlobFallback } from "@/hooks/useVideoBlobFallback";
import {
  GALLERY_GRID_CLASS,
  galleryItemAlt,
  galleryItemIsVideo,
  galleryMediaUrl,
  galleryProxyUrl,
  getGalleryThumbnailUrl,
} from "@/components/gallery/gallery-prerender";
import type { GalleryPrerenderItem } from "@/components/gallery/gallery-prerender";

const GalleryLightbox = dynamic(
  () => import("./GalleryLightbox").then((m) => ({ default: m.GalleryLightbox })),
  { ssr: false, loading: () => <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"><Skeleton className="h-[60vh] w-[80vw] rounded-lg" /></div> }
);

/**
 * GALCPU-1: the item carries the R2 KEY, not pre-built URLs.
 *
 * `/gallery` is `force-dynamic` and receives the entire index, so each field
 * here is React-Flight-serialised once per item on every view. The three URL
 * fields this used to carry (`imageUrl`, `imageUrlLightbox`, `imageUrlRaw` —
 * the last two byte-identical on every image item) were 287,896 of the
 * 738,770-byte production page. They are rebuilt from `imageKey` + the
 * page-level `cdnBase` at the point of render, which is also strictly less
 * client work: only the ~12-48 VISIBLE tiles ever need a URL, not all 1,084.
 *
 * GALMEDIA-1 goes one further and drops `mediaType` (a further 26,925 bytes,
 * measured on the live page) — `galleryItemIsVideo(item)` reads it back off the
 * key's extension. See its doc-block for why the two can never disagree.
 *
 * This IS `GalleryPrerenderItem` — the interactive grid and the server-rendered
 * static grid must consume byte-identical items or the hydration swap re-fetches
 * every above-the-fold thumbnail (GALSSR). It is an alias rather than a second
 * declaration precisely so the two cannot drift; `GalleryPrerenderItem` is in
 * turn a `Pick<>` of the canonical `GalleryItem` in `lib/gallery-types.ts`, so a
 * rename on the stored shape fails the type-check here too.
 */
export type GalleryItem = GalleryPrerenderItem;

function GalleryInfoContent() {
  return (
    <div className="space-y-4 text-sm leading-relaxed text-muted-foreground">
      <p>
        Bespoke floral arrangements from The Floral Muse, Leeds — fresh bouquets (Leeds & West Yorkshire), preserved flowers, and hand-crocheted florals (UK-wide). All made to order.{" "}
        <Link href="/products" className="font-medium text-primary underline underline-offset-2 hover:no-underline">
          Browse products
        </Link>{" "}
        or{" "}
        <Link href="/enquire" className="font-medium text-primary underline underline-offset-2 hover:no-underline">
          enquire
        </Link>{" "}
        with your occasion.
      </p>
      <p>
        For birthdays, anniversaries, sympathy, get well, thank you, or just because.{" "}
        <Link href="/flower-delivery-leeds" className="font-medium text-primary underline underline-offset-2 hover:no-underline">
          Flower delivery across Leeds
        </Link>{" "}
        for fresh bouquets; preserved and crochet shipped UK-wide.
      </p>
    </div>
  );
}

/* ── Shared IntersectionObserver for all LazyThumbnail instances ── */
let sharedObserver: IntersectionObserver | null = null;
const observerCallbacks = new Map<Element, () => void>();

function getSharedObserver() {
  if (!sharedObserver) {
    sharedObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cb = observerCallbacks.get(entry.target);
            if (cb) {
              cb();
              observerCallbacks.delete(entry.target);
              sharedObserver?.unobserve(entry.target);
            }
          }
        });
      },
      { rootMargin: "200px", threshold: 0.01 }
    );
  }
  return sharedObserver;
}

/** Lazy thumbnail with IntersectionObserver; uses optimized thumbnail URL for grid.
 *
 *  NOTE (GALSSR): the thumbnail URL builder now lives in
 *  `components/gallery/gallery-prerender.ts` because the server-rendered static
 *  grid must request the byte-identical URL — otherwise the hydration swap
 *  re-downloads every above-the-fold thumbnail. Do not re-inline it here. */
const LazyThumbnail = memo(function LazyThumbnail({
  src,
  alt,
  className = "",
  eager = false,
  priority = false,
}: {
  src: string;
  alt: string;
  className?: string;
  /** Render the <img> immediately (no IntersectionObserver wait). Set on the
   *  first screenful so the alt text + image are present in the SSR HTML for
   *  crawlers + assistive tech (the rest stay lazy below the fold). */
  eager?: boolean;
  /** Mark as the LCP candidate (adds fetchpriority=high + a preload). Set on the
   *  single first grid image only so /gallery has a prioritised, SSR-visible LCP. */
  priority?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(eager);
  const [loaded, setLoaded] = useState(false);
  const [useFull, setUseFull] = useState(false);
  const [failed, setFailed] = useState(false);
  const thumbnailUrl = getGalleryThumbnailUrl(src);
  const imgSrc = useFull ? src : thumbnailUrl;

  useEffect(() => {
    if (eager) return; // already rendering — no observer needed
    const el = ref.current;
    if (!el) return;
    const observer = getSharedObserver();
    observerCallbacks.set(el, () => setInView(true));
    observer.observe(el);
    return () => {
      observerCallbacks.delete(el);
      observer.unobserve(el);
    };
  }, [eager]);

  if (!src || failed) {
    return (
      <div className="relative flex aspect-4/5 w-full flex-col items-center justify-center gap-2 overflow-hidden bg-muted text-muted-foreground animate-fade-in">
        <ImageOff className="h-6 w-6 opacity-40" />
        <span className="text-xs font-medium opacity-60">Image unavailable</span>
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className={`relative aspect-4/5 w-full overflow-hidden ${className}`.trim()}
    >
      {!loaded && <Skeleton className="absolute inset-0 rounded-none" />}
      {inView && (
        <Image
          src={imgSrc}
          alt={alt}
          fill
          /* Must mirror the grid's real column counts (2 / 4 / 5 / 6 — see the
             grid wrapper below) so Next picks the right srcset candidate once
             Cloudflare Image Resizing is enabled. The old "50vw, 33vw" assumed
             a 2/3-column grid and told the browser to fetch images ~2× wider
             (≈4× the pixels) than the cells actually render at md+. */
          sizes="(max-width: 768px) 50vw, (max-width: 1280px) 25vw, (max-width: 1536px) 20vw, 16vw"
          priority={priority}
          /* Skip Next.js optimisation for absolute URLs — both direct R2
             originals and (when enabled) already-resized CF Image-Resizing
             URLs are absolute (built off siteUrl), so this covers both. */
          unoptimized={imgSrc.startsWith("http")}
          onLoad={() => setLoaded(true)}
          onError={() => {
            if (!useFull) setUseFull(true);
            else setFailed(true);
          }}
          // Eager (first-screenful) thumbnails paint at full opacity
          // immediately — the opacity-0 -> opacity-100 fade only delayed the
          // first visible paint (hurts LCP) for no benefit above the fold.
          // Below-the-fold thumbnails keep the fade-in on decode.
          className={`object-cover ${
            eager
              ? "opacity-100"
              : `transition-opacity duration-500 ease-out ${
                  loaded ? "opacity-100" : "opacity-0"
                }`
          }`}
        />
      )}
    </div>
  );
});

/** Lazy-loads video only when in viewport to avoid loading many videos at once */
function LazyVideo({
  src,
  srcDirect,
  className,
  children,
}: {
  src: string;
  srcDirect?: string;
  className?: string;
  children: React.ReactNode;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const primarySrc = srcDirect || src;
  // Grid previews opt out of the blob fallback — a transient <video> error must
  // never trigger a whole-file (up to 100 MB) download in a grid. The primary
  // src is the edge-cached CDN URL, so real errors here are rare.
  const { activeSrc, error, handleError } = useVideoBlobFallback(
    shouldLoad ? primarySrc : undefined,
    undefined,
    { disableBlobFallback: true },
  );

  // Reuse the single shared IntersectionObserver (see getSharedObserver) instead
  // of creating one observer instance per video — on a gallery with many videos
  // that per-instance churn is avoidable overhead. One-shot: loads once in view.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = getSharedObserver();
    observerCallbacks.set(el, () => setShouldLoad(true));
    observer.observe(el);
    return () => {
      observerCallbacks.delete(el);
      observer.unobserve(el);
    };
  }, []);

  if (!src || error) {
    return (
      <div className={`relative aspect-4/5 ${className ?? ""}`}>
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-muted text-muted-foreground animate-fade-in">
          <VideoOff className="h-6 w-6 opacity-40" />
          <span className="text-xs font-medium opacity-60">Video unavailable</span>
        </div>
        {children}
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`relative ${className ?? ""}`}>
      {!videoReady && <Skeleton className="absolute inset-0" />}
      {shouldLoad && activeSrc ? (
        <video
          key={activeSrc}
          src={activeSrc}
          className={`h-full w-full object-cover transition-opacity duration-500 ${
            videoReady ? "opacity-100" : "opacity-0"
          }`}
          preload="metadata"
          muted
          playsInline
          aria-hidden="true"
          onLoadedMetadata={() => setVideoReady(true)}
          onError={handleError}
        />
      ) : (
        <Skeleton className="h-full w-full" />
      )}
      {children}
    </div>
  );
}

interface GalleryClientProps {
  items: GalleryItem[];
  categories: string[];
  /**
   * GALCPU-1: the R2 public base (`R2_PUBLIC_URL`), supplied ONCE for the whole
   * page instead of being baked into three URL fields on all 1,084 items.
   *
   * It must arrive as a prop and must NOT be re-read here via `lib/r2.ts`:
   * `R2_PUBLIC_URL` is a Worker `var`, not `NEXT_PUBLIC_*`, so the browser never
   * sees it — `getR2ImageUrl()` would return the CDN URL during SSR and the
   * same-origin proxy after hydration, i.e. a guaranteed mismatch. It is a
   * public CDN hostname (`wrangler.jsonc` `vars`), not a secret.
   */
  cdnBase?: string;
  /**
   * GALSSR: the server-rendered first screen (`<GalleryStaticGrid>` from
   * `app/(main)/gallery/page.tsx`). Rendered INSTEAD of the interactive grid
   * until hydration, so a crawler that never runs JS still receives real
   * `<figure>`/`<img>` markup — and so the two grids can never both be in the
   * DOM at once (no duplicate tiles, no duplicate image downloads).
   */
  ssrGrid?: React.ReactNode;
  /** How many leading items `ssrGrid` covers. Becomes the initial visible count
   *  so the swap on hydration neither grows nor shrinks the grid. */
  ssrCount?: number;
}

export function GalleryClient({
  items: initialItems,
  categories,
  cdnBase,
  ssrGrid,
  ssrCount = 0,
}: GalleryClientProps) {
  const pageSize = useGalleryPageSize();
  const filter = useGallery((s) => s.filter);
  const displayCount = useGallery((s) => s.displayCount);
  const lightboxIndex = useGallery((s) => s.lightboxIndex);
  const infoOpen = useGallery((s) => s.infoOpen);
  const setFilter = useGallery((s) => s.setFilter);
  const setDisplayCount = useGallery((s) => s.setDisplayCount);
  const setLightboxIndex = useGallery((s) => s.setLightboxIndex);
  const setInfoOpen = useGallery((s) => s.setInfoOpen);
  const resetForFilter = useGallery((s) => s.resetForFilter);
  const resetGalleryView = useGallery((s) => s.reset);

  /**
   * GALSSR: a floor on the visible count for as long as the view is still the
   * one the server painted. The store's `displayCount` starts at 6 (the mobile
   * page size), so without this floor a 12-tile static grid would shrink to 6
   * for a frame at hydration and then grow back once the page-size effect ran.
   * Dropped the moment the user changes the view (filter / load more), after
   * which the ordinary store-driven paging takes over.
   */
  const [ssrFloor, setSsrFloor] = useState(ssrCount);

  // GALMEDIA-1: this used to `.map()` the whole array on every render purely to
  // normalise `mediaType` onto each item — a per-render allocation of 1,084
  // objects that was itself a source of interaction lag, and which the memo
  // existed only to suppress. The field is gone; `galleryItemIsVideo(item)`
  // derives it at the ~12-48 tiles that actually render, so the prop array is
  // now used as-is and nothing is rebuilt.
  const items = initialItems;

  // Origin of the image CDN (R2 custom domain). Preconnecting to it in the
  // <head> opens the TLS connection before the grid mounts, shaving the first
  // thumbnails' latency. GALCPU-1: read straight off `cdnBase` — this used to
  // scan the whole item array for the first absolute `imageUrlRaw`, a field
  // that no longer exists (and never needed to, to learn one origin).
  const cdnOrigin = useMemo(() => {
    if (!cdnBase?.startsWith("http")) return null;
    try {
      return new URL(cdnBase).origin;
    } catch {
      return null;
    }
  }, [cdnBase]);

  const filters = useMemo(() => ["All", ...categories], [categories]);
  const filtered = useMemo(
    () =>
      filter === "All"
        ? items
        : items.filter(
            (i) => i.category.toLowerCase() === filter.toLowerCase()
          ),
    [filter, items]
  );
  const effectiveCount = Math.max(displayCount, ssrFloor);
  const visibleItems = useMemo(
    () => filtered.slice(0, effectiveCount),
    [filtered, effectiveCount]
  );
  const hasMore = filtered.length > effectiveCount;

  // The LCP thumbnail is the first IMAGE, which is NOT necessarily the first
  // item — the leading gallery items can be videos (they render via LazyVideo,
  // which has no `priority`/fetchpriority hook). Marking `priority` off the raw
  // array index (`index === 0`) meant that when item 0 was a video NO image on
  // the page got `fetchpriority="high"`, losing the LCP hint entirely. Track
  // the first image's index so exactly one image is prioritised.
  const firstImageIndex = useMemo(
    () => visibleItems.findIndex((i) => !galleryItemIsVideo(i)),
    [visibleItems]
  );

  const handleFilterChange = useCallback(
    (f: string) => {
      setSsrFloor(0); // the server-painted view is gone — release the floor
      setFilter(f);
      queueMicrotask(() => resetForFilter(pageSize));
    },
    [setFilter, resetForFilter, pageSize]
  );

  useEffect(() => {
    queueMicrotask(() => setDisplayCount((prev) => Math.max(prev, pageSize)));
  }, [pageSize, setDisplayCount]);

  // This view's state lives in a zustand store, and a zustand store is a MODULE
  // SINGLETON — it outlives this page's mount. Leaving /gallery by client-side
  // navigation with the lightbox open therefore left `lightboxIndex` behind, and
  // the next visit re-opened the lightbox on a photo the visitor never tapped
  // (`infoOpen: true` re-opened the info Sheet/Dialog the same way). A hard load
  // was always fine, because that re-evaluates the module — which is why no test
  // caught it. Clear it on unmount so each mount starts fresh. Same fix, same
  // shape, as `resetOverlay` in app/(main)/products/ProductsClient.tsx.
  //
  // `reset()` is used rather than `resetForFilter`/`closeLightbox` because those
  // clear only part of the state (`resetForFilter` keeps `filter` and `infoOpen`)
  // and would need the page size, which on unmount belongs to the page being
  // navigated TO.
  useEffect(() => () => resetGalleryView(), [resetGalleryView]);

  /**
   * GALCPU-1: resolve the lightbox's URLs for the ONE open item.
   *
   * `GalleryLightbox`'s props are deliberately unchanged — it still receives
   * `imageUrl` / `imageUrlLightbox` / `imageUrlRaw`, with exactly the same
   * values and the same fallbacks the server used to bake into all 1,084 items
   * (`imageUrlRaw: rawUrl || undefined` for video, `imageUrlLightbox: rawUrl ||
   * proxyUrl` for images). The only change is that they are computed once, on
   * demand, instead of 1,084 times per page view.
   */
  const lightboxItem = useMemo(() => {
    if (lightboxIndex === null) return null;
    const item = filtered[lightboxIndex];
    if (!item) return null;
    const proxyUrl = galleryProxyUrl(item.imageKey);
    const rawUrl = cdnBase ? galleryMediaUrl(item.imageKey, cdnBase) : "";
    return {
      ...item,
      // GALMEDIA-1: `GalleryLightbox`'s props are unchanged — it still takes a
      // literal `mediaType`. It is resolved here, once, for the ONE open item.
      mediaType: (galleryItemIsVideo(item) ? "video" : "image") as "image" | "video",
      imageUrl: proxyUrl,
      imageUrlLightbox: rawUrl || proxyUrl,
      imageUrlRaw: rawUrl || undefined,
    };
  }, [filtered, lightboxIndex, cdnBase]);

  const filteredLen = filtered.length;
  const goNext = useCallback(() => {
    setLightboxIndex((prev) => {
      if (prev === null) return null;
      return prev < filteredLen - 1 ? prev + 1 : 0;
    });
  }, [filteredLen, setLightboxIndex]);

  const goPrev = useCallback(() => {
    setLightboxIndex((prev) => {
      if (prev === null) return null;
      return prev > 0 ? prev - 1 : filteredLen - 1;
    });
  }, [filteredLen, setLightboxIndex]);

  const closeLightbox = useCallback(() => {
    setLightboxIndex(null);
  }, [setLightboxIndex]);

  // NOTE: keyboard navigation (Esc / ←  / →) lives ONLY in GalleryLightbox,
  // which is the component that owns the open lightbox UI. A second listener
  // here (calling the same goNext/goPrev/close callbacks) double-fired every
  // arrow press while the lightbox was open — the gallery appeared to skip an
  // image. Do NOT re-add a keydown listener in this component.

  const isMobile = useIsMobile();
  const mounted = useSyncExternalStore(() => () => {}, () => true, () => false);
  const useMobileLayout = mounted && isMobile;

  return (
    <div className="px-6 py-8 md:px-10 md:py-12 lg:px-16">
      {/* Warm the TLS connection to the image CDN before the grid mounts.
          React 19 hoists this <link> into <head>. */}
      {cdnOrigin && <link rel="preconnect" href={cdnOrigin} />}
      <div className="mx-auto max-w-7xl">
        <div className="space-y-1 sm:space-y-2">
          <p className="hidden text-base leading-relaxed text-muted-foreground sm:block">
            Browse our gallery of bespoke floral arrangements from The Floral Muse, Leeds.
            We create fresh flower bouquets, preserved flowers, and hand-crocheted
            florals for birthdays, weddings, sympathy, and every occasion. All pieces
            are made to order — explore our{" "}
            <ProseLink href="/products">
              products
            </ProseLink>{" "}
            or{" "}
            <ProseLink href="/enquire">
              enquire
            </ProseLink>{" "}
            with your idea.
          </p>
          <button
            type="button"
            onClick={() => setInfoOpen(true)}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-primary underline-offset-2 hover:underline sm:text-sm sm:text-muted-foreground sm:hover:text-primary"
          >
            <Info className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" />
            About our gallery & delivery
          </button>
        </div>

        {useMobileLayout ? (
          <Sheet open={infoOpen} onOpenChange={setInfoOpen}>
            <SheetContent
              side="bottom"
              className="flex max-h-[85dvh] flex-col rounded-t-2xl border-border bg-background p-0"
              closeButtonClassName="border-border text-foreground hover:bg-muted"
            >
              <SheetHeader className="shrink-0 border-b border-border px-4 py-3">
                <SheetTitle className="text-base font-semibold">About our gallery & delivery</SheetTitle>
                <SheetDescription className="sr-only">About the gallery and delivery information</SheetDescription>
              </SheetHeader>
              <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
                <GalleryInfoContent />
              </div>
            </SheetContent>
          </Sheet>
        ) : (
          <Dialog open={infoOpen} onOpenChange={setInfoOpen}>
            <DialogContent className="max-h-[85dvh] max-w-2xl gap-0 overflow-hidden p-0 sm:max-w-2xl">
              <DialogHeader className="shrink-0 border-b border-border px-6 py-4">
                <DialogTitle>About our gallery & delivery</DialogTitle>
                <DialogDescription className="sr-only">About the gallery and delivery information</DialogDescription>
              </DialogHeader>
              <div className="overflow-y-auto px-6 py-4">
                <GalleryInfoContent />
              </div>
            </DialogContent>
          </Dialog>
        )}

        <div className="mt-4 sm:mt-8">
          <div className="transition-opacity duration-300">
            <div className="flex flex-wrap gap-2">
              {filters.map((f) => (
                <FilterChip
                  key={f}
                  active={filter === f}
                  onClick={() => handleFilterChange(f)}
                >
                  {f}
                </FilterChip>
              ))}
            </div>

            {filtered.length === 0 ? (
              <div className="mt-8 space-y-6 py-8 sm:mt-12 sm:py-16">
                <p className="text-base leading-relaxed text-muted-foreground">
                  <span className="hidden sm:inline">
                    Our gallery showcases the types of arrangements we create at The Floral Muse:
                    fresh flower bouquets delivered across Leeds and West Yorkshire, preserved
                    flowers with character and plush-inspired designs, and hand-crocheted
                    florals that last 3+ years — indefinite with proper care. Each piece is
                    designed individually for the
                    occasion — birthdays, anniversaries, sympathy, get well, thank you, and
                    just because. We are a Leeds-based florist operating online. We deliver
                    fresh flowers across Leeds and West Yorkshire; preserved flowers and
                    crochet ship UK-wide. New arrangements are added regularly.{" "}
                  </span>
                  <span className="sm:hidden">No arrangements in this category yet. </span>
                  <ProseLink href="/products">
                    Browse products
                  </ProseLink>
                  {" "}or{" "}
                  <ProseLink href="/enquire">
                    enquire
                  </ProseLink>{" "}
                  with your idea.
                </p>
                <div className="flex flex-wrap justify-center gap-4 pt-2 sm:pt-4">
                  <Button asChild>
                    <Link href="/products">Browse products</Link>
                  </Button>
                  <Button asChild variant="outline">
                    <Link href="/enquire">Enquire</Link>
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {/* GALSSR: before hydration the grid IS the server-rendered
                    static one — never both, so tiles and image requests are
                    never duplicated. `mounted` is the existing
                    useSyncExternalStore flag (false on the server, true after
                    hydration), so React swaps the two in a single commit: the
                    tiles are already painted and the thumbnails already cached,
                    which is why there is no flash and no layout shift. */}
                {!mounted && ssrGrid ? (
                  ssrGrid
                ) : (
                <div
                  className={GALLERY_GRID_CLASS}
                  data-testid="gallery-grid"
                >
                  {visibleItems.map((item, index) => (
                    <Button
                      key={item.id}
                      type="button"
                      variant="ghost"
                      onClick={() => setLightboxIndex(index)}
                      aria-label={item.title || `${item.category || "Gallery"} arrangement`}
                      data-testid="gallery-card"
                      className={`group h-auto w-full cursor-pointer overflow-hidden rounded-lg bg-muted p-0 text-left hover:bg-muted ${
                        // No entrance animation on the tiles the server already
                        // painted — animating them in would BE the flash the
                        // swap is designed to avoid.
                        ssrFloor === 0 && index < pageSize ? "animate-gallery-item" : ""
                      }`}
                      style={
                        ssrFloor === 0 && index < pageSize
                          ? { animationDelay: `${Math.min(index * 0.03, 0.3)}s` }
                          : undefined
                      }
                    >
                      {galleryItemIsVideo(item) ? (
                        <LazyVideo
                          /* GALCPU-1: derived per VISIBLE tile from the item's
                             R2 key + the page-level cdnBase, not from three
                             URL fields serialised onto every item. */
                          src={galleryProxyUrl(item.imageKey)}
                          srcDirect={cdnBase ? galleryMediaUrl(item.imageKey, cdnBase) : undefined}
                          className="relative aspect-4/5 bg-foreground"
                        >
                          <div className="absolute inset-0 flex items-center justify-center transition-transform duration-300 ease-out group-hover:scale-105">
                            <div className="rounded-full bg-black/50 p-4 backdrop-blur-sm">
                              <Play className="h-8 w-8 text-white" fill="white" />
                            </div>
                          </div>
                          <div className="absolute left-3 top-3 flex items-center gap-1 rounded-full bg-black/60 px-2.5 py-1 text-xs font-medium text-white backdrop-blur-sm">
                            <Film className="h-3 w-3" />
                            Video
                          </div>
                        </LazyVideo>
                      ) : (
                        <div className="w-full overflow-hidden transition-transform duration-300 ease-out group-hover:scale-105">
                          <LazyThumbnail
                            /* GALCPU-1: `galleryMediaUrl` reproduces the old
                               `imageUrlRaw || imageUrl` precedence exactly — it
                               returns the CDN URL when a base is configured and
                               the same-origin proxy when it is not. */
                            src={galleryMediaUrl(item.imageKey, cdnBase)}
                            /* Shared with the static grid so the accessibility
                               tree does not change at hydration (GALSSR). */
                            alt={galleryItemAlt(item)}
                            /* Every tile the server already painted must render
                               its <img> immediately — leaving one behind the
                               IntersectionObserver would replace a visible photo
                               with a skeleton the instant we hydrate. */
                            eager={index < Math.max(8, ssrFloor)}
                            priority={index === firstImageIndex}
                          />
                        </div>
                      )}
                    </Button>
                  ))}
                </div>
                )}

                <div className="mt-8 flex flex-col gap-4 sm:mt-12 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-muted-foreground" aria-live="polite" aria-atomic="true">
                    Showing {visibleItems.length} of {filtered.length}
                  </p>
                  {hasMore && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        // Fold the SSR floor into the store before releasing it,
                        // so "Load more" adds a page instead of collapsing the
                        // grid back to the store's smaller count.
                        setSsrFloor(0);
                        setDisplayCount(effectiveCount + pageSize);
                      }}
                      className="border-border text-primary hover:bg-primary/10"
                    >
                      Load more
                    </Button>
                  )}
                </div>

                {/* CTA */}
                <div className="mt-12 rounded-2xl bg-muted px-6 py-12 text-center sm:mt-16 sm:px-10 md:py-16">
                  <p className="label-overline text-muted-foreground">Like what you see?</p>
                  <h2 className="heading-section mt-3">Let&rsquo;s create something for you</h2>
                  <p className="mx-auto mt-3 max-w-lg text-sm text-muted-foreground sm:text-base">
                    Every arrangement is made to order. Tell us your colours, occasion, and budget — we&rsquo;ll design something unique.
                  </p>
                  <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
                    <Button asChild size="lg" className="w-full sm:w-auto sm:min-w-48">
                      <Link href="/enquire">Enquire now</Link>
                    </Button>
                    <Button asChild variant="outline" size="lg" className="w-full sm:w-auto sm:min-w-48">
                      <Link href="/products">Browse products</Link>
                    </Button>
                  </div>
                  <p className="mt-6 text-sm text-muted-foreground">
                    Read our{" "}
                    <ProseLink href="/flower-care">flower care guide</ProseLink>,{" "}
                    learn about our{" "}
                    <ProseLink href="/sustainability">sustainability practices</ProseLink>,{" "}
                    or find out more{" "}
                    <ProseLink href="/about">about us</ProseLink>.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {lightboxItem && lightboxIndex !== null && (
        <GalleryLightbox
          item={lightboxItem}
          index={lightboxIndex}
          total={filtered.length}
          open={true}
          onClose={closeLightbox}
          onPrev={goPrev}
          onNext={goNext}
        />
      )}
    </div>
  );
}
